import { BreadcrumComponent } from '../breadcrum/breadcrum.component';
import {
  Component,
  OnInit,
  ViewChild,
  ElementRef,
  ChangeDetectorRef,
} from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';
import { ApiService } from '../../shared/services/api.service';
import { LoaderService } from '../../shared/services/loader.service';
import { CartService } from '../../shared/services/cart.service';
import { BuyNowButtonComponent } from '../buy-now-button/buy-now-button.component';
import { BuyNowPopupComponent } from '../buy-now-popup/buy-now-popup.component';
import { ScrapCertificateService } from '../../shared/services/scrap-certificate.service';
import { ScrapCertificateModalComponent } from '../../shared/components/scrap-certificate-modal/scrap-certificate-modal.component';
import { BehaviourSubjectService } from '../../shared/services/behaviour-subject.service';
import { DeliveryDateService } from '../../shared/services/delivery-date.service';

interface SellerProduct {
  id: string;
  image: string;
  price: number;
  originalPrice: number;
  discount: number;
  discountText: string;
  sellerName: string;
  sellerLocation: string;
  grade: string;
  gradeDescription: string;
  deliveryDate: string;
  deliveryText: string;
  part_number?: string;
}

interface ProductComparisonData {
  title: string;
  subtitle: string;
  breadcrumb: string;
  sellers: SellerProduct[];
}

@Component({
  selector: 'app-view-other',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    BuyNowButtonComponent,
    BuyNowPopupComponent,
    ScrapCertificateModalComponent,
    BreadcrumComponent,
  ],
  templateUrl: './view-other.component.html',
  styleUrl: './view-other.component.scss',
})
export class ViewOtherComponent implements OnInit {
  viewOtherBreadcrumbs: any[] = [{ label: 'Parts', url: '/product-list' }, { label: 'Product Comparison', url: null }];
  @ViewChild('productCardsContainer') productCardsContainer!: ElementRef;
  @ViewChild('tableContainer') tableContainer!: ElementRef;

  productDetailData: any;
  sub: Subscription = new Subscription();
  products: any;
  productData: any;
  text1: any;
  limit: any;

  // Dynamic data properties
  comparisonData: ProductComparisonData = {
    title: '',
    subtitle: '',
    breadcrumb: '',
    sellers: [],
  };

  // Scrolling properties
  currentCardIndex = 0;
  cardsPerView = 4; // Number of cards visible at once

  // Buy Now and Cart properties
  isBuyNowPopupOpen = false;
  selectedProductForBuyNow: any = null;
  itemAddedToCart = new Map<string, boolean>(); // Track success state for each product by part number
  showScrapCertificateModal = false;

  constructor(
    private router: Router,
    private apiService: ApiService,
    private loaderService: LoaderService,
    private cartService: CartService,
    private scrapCertificateService: ScrapCertificateService,
    private behaviourSubject: BehaviourSubjectService,
    private cdr: ChangeDetectorRef,
    private deliveryDateService: DeliveryDateService,
  ) {
    this.productDetailData = JSON.parse(
      localStorage.getItem('viewOther') || '{}',
    );
  }

  ngOnInit(): void {
    // localStorage.removeItem('viewOther');
    this.getProductList();

    // Sync scrap certificate modal state with shared service
    this.sub.add(
      this.scrapCertificateService.showScrapCertificateModal$.subscribe(
        (show) => {
          this.showScrapCertificateModal = show;
          this.cdr.detectChanges();
        },
      ),
    );
  }

  getProductList() {
    this.loaderService.show();
    // Scroll to top when product list is refreshed
    // this.scrollToTop();

    try {
      let data = {
        make: this.productDetailData?.part_detail?.make,
        variant: this.productDetailData?.part_detail?.variant,
        table_name: this.productDetailData?.part_detail?.table_name,
        model: this.productDetailData?.part_detail?.model,
        exclude_seller_code: this.productDetailData?.seller_detail?.seller_code,
        // action_type: 'exclude_seller_code'
      };

      this.sub.add(
        this.apiService.getProductlistData(data).subscribe({
          next: (value) => {
            if (value.success) {
              this.products = value.data.part_list;
              this.productData = value.data.part_list;
              this.text1 = value.data.part_list[0].short_desc_s;

              // Initialize carousel properties for each product
              this.products.forEach((product: any) => {
                if (Array.isArray(product.part_image_path)) {
                  product.images = product.part_image_path;
                } else if (product.part_image_path) {
                  product.images = [product.part_image_path];
                } else {
                  product.images = [];
                }
                product.currentImageIndex = 0;
              });

              // Transform data for dynamic binding
              this.transformDataForComparison();
            } else {
              this.products = [];
            }
            this.loaderService.hide();
          },
          error: (err) => {
            console.error('Error loading products:', err);
            // Don't hide loader here as it's handled in the error interceptor
            // Only hide if it's a specific error we want to handle differently
            if (err.message === 'Request cancelled') {
            }
          },
          complete: () => {
            // Ensure loader is hidden in complete callback as well
            this.loaderService.hide();
          },
        }),
      );
    } catch (error) {
      console.error('Error in products:', error);
      this.loaderService.hide();
      throw error;
    }
  }

  transformDataForComparison() {
    if (!this.products || this.products.length === 0) {
      return;
    }

    // Set title and subtitle
    this.comparisonData.title =
      this.productDetailData?.part_detail?.short_desc_s || 'Product Comparison';
    this.comparisonData.subtitle = `${this.products.length} sellers are selling the same product`;
    this.comparisonData.breadcrumb = `Home > Parts > ${this.productDetailData?.part_detail?.short_desc_s || 'Product'} > Check with different ${this.productDetailData?.seller_detail?.seller_name || 'Sellers'}`;

    // Transform products into seller data
    this.comparisonData.sellers = this.products.map(
      (product: any, index: number) => {
        // const discount = product.mrp && product.selling_price ?
        //   Math.round(((product.mrp - product.selling_price) / product.mrp) * 100) : 0;

        return {
          id: product.id || `seller-${index}`,
          image:
            product.part_image_path?.[0] ||
            product.part_image_path ||
            'https://placehold.co/280x200',
          price: product?.discounted_price
            ? product?.discounted_price
            : product?.price_s || 0,
          originalPrice: product?.discounted_price ? product?.price_s : 0,
          discount: product?.discount_percentage,
          discountText:
            product?.discount_percentage > 0
              ? `${product?.discount_percentage}% Off`
              : '',
          sellerName: product?.seller_name || 'RVSF',
          sellerLocation: `${product?.seller_name || 'RVSF'}, ${product?.DISTRICT_s || ''}`, //, ${product?.pincode||''}`,
          grade: product?.quality_s,
          // gradeDescription: 'Certified '+product?.quality_s || 'C'+'-grade, reliable & tested.',
          deliveryDate: this.calculateDeliveryDate(index),
          deliveryText: `Get Delivery by ${this.calculateDeliveryDate(index)}`,
          part_number: product?.part_number,
        };
      },
    );
  }

  calculateDeliveryDate(index: number): string {
    const today = new Date();
    const deliveryDays = [3, 5, 7, 10, 12]; // Different delivery times for different sellers
    const deliveryDay = deliveryDays[index % deliveryDays.length];
    const deliveryDate = new Date(today);
    deliveryDate.setDate(today.getDate() + deliveryDay);

    const day = deliveryDate.getDate();
    const suffix = this.getDaySuffix(day);
    const month = deliveryDate.toLocaleDateString('en-US', { month: 'short' });

    return `${day}${suffix} ${month}`;
  }

  getDaySuffix(day: number): string {
    if (day >= 11 && day <= 13) return 'th';
    switch (day % 10) {
      case 1:
        return 'st';
      case 2:
        return 'nd';
      case 3:
        return 'rd';
      default:
        return 'th';
    }
  }

  onPreviousPage() {
    if (this.currentCardIndex > 0) {
      this.currentCardIndex--;
      this.scrollToCard(this.currentCardIndex);
      this.scrollTableToSeller(this.currentCardIndex);
    }
  }

  onNextPage() {
    const maxIndex = this.comparisonData.sellers.length - this.cardsPerView;
    if (this.currentCardIndex < maxIndex) {
      this.currentCardIndex++;
      this.scrollToCard(this.currentCardIndex);
      this.scrollTableToSeller(this.currentCardIndex);
    }
  }

  scrollToCard(index: number) {
    if (this.productCardsContainer) {
      const cardWidth = 288; // w-72 = 288px
      const gap = 8; // gap-2 = 8px
      const scrollPosition = index * (cardWidth + gap);
      this.productCardsContainer.nativeElement.scrollTo({
        left: scrollPosition,
        behavior: 'smooth',
      });
    }
  }

  scrollTableToSeller(sellerIndex: number) {
    if (this.tableContainer) {
      const columnWidth = 288; // w-72 = 288px
      const scrollPosition = sellerIndex * columnWidth;
      this.tableContainer.nativeElement.scrollTo({
        left: scrollPosition,
        behavior: 'smooth',
      });
    }
  }

  onCardClick(index: number) {
    this.currentCardIndex = index;
    this.scrollTableToSeller(index);
  }

  onBuyNow(productData: any) {
    // Check if this is a scrap part
    if (this.scrapCertificateService.isScrapPart(productData)) {
      // Check if user is logged in first
      let userData = {};
      try {
        const userDataString = localStorage.getItem('userData1');
        if (userDataString && !userDataString.startsWith('U2FsdGVkX1')) {
          userData = JSON.parse(userDataString);
        }
      } catch (error) {
        console.error('Error parsing userData1 from localStorage:', error);
      }
      if (!userData || !(userData as any)?.id) {
        this.openLoginPopup();
        return;
      }

      // For scrap parts, check certificate match before proceeding to buy now
      this.scrapCertificateService.handleAddToCart(
        productData,
        () => {
          // Callback for when certificate matches - proceed with buy now
          this.selectedProductForBuyNow = productData;
          this.isBuyNowPopupOpen = true;
        },
        () => {
          // Callback for login required
          this.openLoginPopup();
        },
      );
      return;
    }

    // For non-scrap parts, proceed with buy now directly
    this.selectedProductForBuyNow = productData;
    this.isBuyNowPopupOpen = true;
  }

  onPaymentSuccess(event: any) {
    // Hide any active loaders before navigation
    this.loaderService.hide();
    this.closeBuyNowPopup();

    // Navigate to order confirmation page with order details
    if (event && event.data) {
      this.router.navigate(['/order-confirmation'], {
        state: { orderDetail: event.data },
      });
    } else {
      // Fallback navigation if no order details
      this.router.navigate(['/order-confirmation']);
    }
  }

  closeBuyNowPopup() {
    this.isBuyNowPopupOpen = false;
    this.selectedProductForBuyNow = null;
  }

  /**
   * Gets the quantity value for the buy now popup
   * Handles both kg and ton units properly
   */
  getQuantityForPopup(product: any): number {
    if (!product) return 1;

    if (product.selectedUnit === 'kg') {
      // For kg units, return custom kg quantity or default to 1
      return product.customKgQuantity
        ? parseFloat(product.customKgQuantity)
        : 1;
    } else {
      // For ton units, return the regular quantity
      return product.quantity || 1;
    }
  }

  addToCart(event: Event, partNumber: string) {
    event.stopPropagation(); // Prevent navigation to product details

    // Find the product data
    const productData = this.products.find(
      (product: any) => product.part_number === partNumber,
    );
    if (!productData) {
      console.error('Product data not found for partNumber:', partNumber);
      return;
    }

    // Use the shared service to handle scrap part detection
    this.cartService.handleAddToCart(
      productData, // Pass the product data
      'Store Pickup',
      this.getQuantityForPopup(productData),
      productData.selectedUnit === 'kg' ? 'Kg' : 'Ton',
      () => {
        // Callback for non-scrap parts
        this.addToCartForNonScrapParts(partNumber, productData);
      },
      () => {
        // Callback for login required
        this.openLoginPopup();
      },
    );
  }

  private addToCartForNonScrapParts(
    partNumber: string,
    productData: any,
  ): void {
    // Check if user is logged in
    let userData = {};
    try {
      const userDataString = localStorage.getItem('userData1');
      if (userDataString && !userDataString.startsWith('U2FsdGVkX1')) {
        userData = JSON.parse(userDataString);
      }
    } catch (error) {
      console.error('Error parsing userData1 from localStorage:', error);
    }
    if (!userData || !(userData as any)?.id) {
      // Get quantity and unit for scrap parts
      const quantity = this.getQuantityForPopup(productData);
      const unit = productData.selectedUnit === 'kg' ? 'Kg' : 'Ton';

      // Add to local storage for non-logged in users with complete product data
      this.cartService.addToLocalStorage(
        productData,
        'Store Pickup',
        quantity,
        unit,
      );
      this.itemAddedToCart.set(partNumber, true); // Mark as added for non-logged in users
      return;
    }

    // Get quantity and unit for scrap parts
    const quantity = this.getQuantityForPopup(productData);
    const unit = productData.selectedUnit === 'kg' ? 'Kg' : 'Ton';

    this.sub.add(
      this.cartService
        .addToCart(partNumber, 'Store Pickup', quantity, unit)
        .subscribe({
          next: () => {
            this.itemAddedToCart.set(partNumber, true); // Set success state
            // Removed automatic navigation to cart after 3 seconds
          },
          error: (err) => {
            console.error('Error adding to cart:', err);
            this.itemAddedToCart.set(partNumber, false); // Mark as failed
          },
          complete: () => {
            // No loader needed
          },
        }),
    );
  }

  goToCart() {
    this.cartService.navigateToCart();
  }

  openLoginPopup() {
    // Use the shared login modal service
    this.behaviourSubject.openLoginModal('other');
  }

  goToCartWithEvent(event: Event) {
    event.stopPropagation(); // Prevent navigation to product details
    this.cartService.navigateToCart();
  }

  /**
   * Get formatted delivery date (3 business days from today)
   */
  getDeliveryDate(): string {
    return this.deliveryDateService.calculateDeliveryDate(3);
  }
}
